/**
 * BlockEditorCard: one editable block within the editor's block list.
 *
 * P5 wave 3a (admin content editor). Renders:
 *   - a header with the block-type label/icon and reorder + delete controls;
 *   - the type-specific edit form, resolved from `blockEditorRegistry`.
 *
 * Reordering uses up/down buttons (accessible, keyboard-reachable; the
 * ContentEditorPage owns the actual array move + `order` re-sequencing).
 * The first block's "up" and the last block's "down" are disabled.
 *
 * Delete routes through a small in-app confirm modal (never window.confirm),
 * owned by this card so the confirm is scoped to the specific block.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, ButtonGroup, Badge, Modal } from 'react-bootstrap';

import { getBlockEditorForm } from './blockEditorRegistry';
import { getBlockTypeDescriptor, getBlockTypeLabel } from './blockTypes';

export default function BlockEditorCard({
    block,
    index,
    isFirst,
    isLast,
    collapsed,
    onToggleCollapsed,
    onChangeData,
    onMoveUp,
    onMoveDown,
    onDelete,
}) {
    const [showConfirm, setShowConfirm] = useState(false);

    const descriptor = getBlockTypeDescriptor(block.type);
    const FormComponent = getBlockEditorForm(block.type);
    const label = getBlockTypeLabel(block.type);

    return (
        <Card className="mb-3 content-editor-block">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <button
                    type="button"
                    className="btn btn-link p-0 text-start text-decoration-none text-dark d-flex align-items-center gap-2 flex-grow-1"
                    onClick={onToggleCollapsed}
                    aria-expanded={!collapsed}
                >
                    <span className="text-muted content-editor-block__chevron">
                        {collapsed ? '▶' : '▼'}
                    </span>
                    {descriptor && (
                        <span aria-hidden="true">{descriptor.icon}</span>
                    )}
                    <strong>{label}</strong>
                    <Badge bg="light" text="dark">
                        #{index + 1}
                    </Badge>
                </button>
                <span className="d-flex gap-2 ms-2">
                    <ButtonGroup size="sm">
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={onMoveUp}
                            disabled={isFirst}
                            aria-label="Move block up"
                        >
                            ↑
                        </Button>
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={onMoveDown}
                            disabled={isLast}
                            aria-label="Move block down"
                        >
                            ↓
                        </Button>
                    </ButtonGroup>
                    <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setShowConfirm(true)}
                    >
                        Delete
                    </Button>
                </span>
            </Card.Header>
            {!collapsed && (
                <Card.Body>
                    {FormComponent ? (
                        <FormComponent
                            data={block.data || {}}
                            onChange={onChangeData}
                        />
                    ) : (
                        <p className="text-muted mb-0">
                            Unknown block type &quot;{block.type}&quot;: this
                            block cannot be edited on this version of the
                            editor.
                        </p>
                    )}
                </Card.Body>
            )}

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete block</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Delete this <strong>{label}</strong> block? This change
                    becomes permanent once you save the draft.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowConfirm(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            setShowConfirm(false);
                            onDelete();
                        }}
                    >
                        Delete block
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}

BlockEditorCard.propTypes = {
    block: PropTypes.shape({
        type: PropTypes.string.isRequired,
        data: PropTypes.object,
        order: PropTypes.number,
    }).isRequired,
    index: PropTypes.number.isRequired,
    isFirst: PropTypes.bool.isRequired,
    isLast: PropTypes.bool.isRequired,
    collapsed: PropTypes.bool.isRequired,
    onToggleCollapsed: PropTypes.func.isRequired,
    onChangeData: PropTypes.func.isRequired,
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};
